import InfoIcon from '@mui/icons-material/Info';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { getIntegration } from 'lib/api/integrations';
import {
  getBalanceReport,
  getCreditLineReport,
  getDataHubReport,
  getDataHubReportForFund,
  getMultiverseReport,
} from 'lib/api/pdf';
import { SectionCard } from 'lib/common/PDF/SectionCard';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import {
  showAlert,
  validateCompanyTypes,
  validatePermissions,
} from 'lib/functions/utils';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { PDFAdapter, PDFAdapterKey } from './adapter';
import { Filters } from './Filters';
import PDFInputs from './inputs';

const SCORING_COMPANIES = [110, 2];

type PDFProps = {
  type: PDFAdapterKey;
  open: boolean;
  onClose: () => void;
  metadata: { id?: number; companyId?: number; businessIdentifier?: string };
};

const PDF: React.FC<PDFProps> = ({ type, open, onClose, metadata }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const display = useMemo(() => PDFAdapter[type], [type]);
  const [sections, setSections] = useState<string[]>([]);
  const [selectedFund, setSelectedFund] = useState<string | undefined>();
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(7, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [observation, setObservation] = useState<string | null>(null);
  const { data: superDebtIntegration } = getIntegration(dispatch, 'super-debt');
  const { data: buroIntegration } = getIntegration(dispatch, 'buro');
  const hasFs =
    validateCompanyTypes(user, 'FactoringFS') &&
    validatePermissions(user, 'financial_states_read');
  const hasScoring = SCORING_COMPANIES.includes(user?.companyId ?? 0);

  const filteredDisplaySections = useMemo(() => {
    return display.sections.filter((section) => {
      if (section.key === 'debt' && !superDebtIntegration?.status) return false;
      if (section.key === 'buro' && !buroIntegration?.status) return false;
      if (section.key === 'balance' && !hasFs) return false;
      if (section.key === 'scoring' && !hasScoring) return false;
      return true;
    });
  }, [display.sections, superDebtIntegration?.status, buroIntegration?.status]);

  const query = useMemo(() => {
    const params = new URLSearchParams({
      sections: sections.join(','),
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
    });
    if (observation) params.append('observation', observation);
    return params.toString();
  }, [sections, selectedFund, startDate, endDate]);

  const { refetch: creditLineReportFn, isFetching: isFetchingCreditLine } =
    getCreditLineReport(dispatch, metadata.id, query);
  const { refetch: dataHubReportFn, isFetching: isFetchingDataHub } =
    getDataHubReport(dispatch, metadata.businessIdentifier, query);
  const { refetch: multiverseReportFn, isFetching: isFetchingMultiverse } =
    getMultiverseReport(
      dispatch,
      metadata.businessIdentifier,
      selectedFund,
      query,
    );
  const {
    refetch: dataHubReportForFundFn,
    isFetching: isFetchingDataHubForFund,
  } = getDataHubReportForFund(
    dispatch,
    metadata.businessIdentifier,
    metadata.companyId,
    selectedFund,
    query,
  );
  const { refetch: balanceReportFn, isFetching: isFetchingBalance } =
    getBalanceReport(dispatch, metadata.businessIdentifier, query);

  const isPending = useMemo(() => {
    switch (type) {
      case 'datahub':
        return isFetchingDataHub;
      case 'multiverse':
        return isFetchingMultiverse;
      case 'creditLine':
        return isFetchingCreditLine;
      case 'datahubForFund':
        return isFetchingDataHubForFund;
      case 'balance':
        return isFetchingBalance;
      default:
        return false;
    }
  }, [
    type,
    isFetchingDataHub,
    isFetchingMultiverse,
    isFetchingCreditLine,
    isFetchingDataHubForFund,
    isFetchingBalance,
  ]);

  const exportReport = async () => {
    if (!sections.length) {
      showAlert(dispatch, 'warning', 'Selecciona al menos una sección');
      return;
    }

    try {
      let file: Blob | undefined;
      switch (type) {
        case 'datahub': {
          if (!metadata.businessIdentifier) {
            showAlert(dispatch, 'warning', 'Selecciona un cliente');
            return;
          }
          const { data } = await dataHubReportFn();
          file = data;
          break;
        }
        case 'multiverse': {
          if (!selectedFund) {
            showAlert(dispatch, 'warning', 'Selecciona un fondo');
            return;
          }
          if (!metadata.businessIdentifier) {
            showAlert(dispatch, 'warning', 'Selecciona un cliente');
            return;
          }
          const { data } = await multiverseReportFn();
          file = data;
          break;
        }
        case 'creditLine': {
          if (!metadata.id) {
            showAlert(dispatch, 'warning', 'Selecciona una línea de crédito');
            return;
          }
          const { data } = await creditLineReportFn();
          file = data;
          break;
        }
        case 'datahubForFund': {
          if (!metadata.companyId || !metadata.businessIdentifier) {
            showAlert(dispatch, 'warning', 'Selecciona una empresa');
            return;
          }
          const { data } = await dataHubReportForFundFn();
          file = data;
          break;
        }
        case 'balance': {
          if (!metadata.businessIdentifier) {
            showAlert(dispatch, 'warning', 'Selecciona un cliente');
            return;
          }
          const { data } = await balanceReportFn();
          file = data;
          break;
        }
      }
      if (!file) {
        showAlert(dispatch, 'error', 'No se pudo generar el reporte');
        return;
      }
      const fileUrl = URL.createObjectURL(file);
      window.open(fileUrl, '_blank');
    } catch (error) {
      showAlert(dispatch, 'error', 'No se pudo generar el reporte');
    }
  };

  const handleToggleSection = (key: string) => {
    setSections((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const allSectionsSelected = sections.length === display.sections.length;

  const handleToggleAllSections = () => {
    if (allSectionsSelected) {
      setSections([]);
    } else {
      setSections(filteredDisplaySections.map((section) => section.key));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        onClose={onClose}
        title={display.title}
        icon={<InfoIcon color="primary" />}
        color="primary"
      />
      {type === 'multiverse' && (
        <>
          <Filters
            selectedFund={selectedFund}
            onFundChange={setSelectedFund}
            startDate={startDate}
            onStartDateChange={setStartDate}
            endDate={endDate}
            onEndDateChange={setEndDate}
          />
          <Divider sx={{ mt: 2, mx: 3 }} />
        </>
      )}
      <DialogContent>
        <Grid container justifyContent="flex-end" sx={{ mb: 1 }}>
          <Grid item>
            <Typography
              variant="caption"
              color="primary"
              sx={{
                cursor: 'pointer',
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
              }}
              onClick={handleToggleAllSections}
            >
              {allSectionsSelected
                ? 'Deseleccionar todas las secciones'
                : 'Seleccionar todas las secciones'}
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={1.25} sx={{ mb: 2 }}>
          {filteredDisplaySections.map((section) => (
            <Grid item xs={12} sm={6} key={section.key}>
              <SectionCard
                title={section.title}
                description={section.description}
                info={section.info}
                selected={sections.includes(section.key)}
                onToggle={() => handleToggleSection(section.key)}
              />
            </Grid>
          ))}
        </Grid>
        {type === 'balance' && (
          <PDFInputs
            observation={observation}
            setObservation={setObservation}
          />
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={onClose} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={exportReport}
          color="primary"
          disabled={isPending}
          loading={isPending}
        >
          Exportar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default PDF;
